import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import AutoRenewalDisclosureModal from "components/AutoRenewalDisclosureModal";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import Switch from "components/Switch";
import { AgreementDetails, SubscriptionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { updateSubscriptionFetcher } from "queries/account.queries";
import { fetchAgreements } from "queries/agreement.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import React, { useMemo, useState } from "react";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";

const AutoRenewalCard = () => {
  const { account, updateFetchAccountBffCache } = useAccount();
  const { mutateAsync: updateSubscription } = useMutation(
    updateSubscriptionFetcher,
  );
  const { data: planPackages } = useFetchPlanPackagesQuery();
  const [pendingSubscription, setPendingSubscription] =
    useState<SubscriptionDetails | null>(null);
  const [pendingAgreement, setPendingAgreement] =
    useState<AgreementDetails | null>(null);

  // Auto-renew is managed per subscription. Hide child (bundled) subscriptions,
  // rolling-billing plans (those renew on their anniversary regardless of
  // the flag; opting out means cancelling the subscription), and plans that
  // don't require auto-renewal consent.
  const autoRenewSubscriptions = useMemo(() => {
    if (!account) return [];
    return account.subscriptions.filter((sub) => {
      if (
        sub.child ||
        sub.status !== "ACTIVE" ||
        !filterIneligibleSubscriptions(sub)
      )
        return false;
      const planPackage = planPackages?.find(
        (p) => p.id === sub.planPackage.id,
      );
      return (
        !!planPackage &&
        !planPackage.rollingBilling &&
        planPackage.requireAutoRenewalConsent
      );
    });
  }, [account, planPackages]);

  const patchAutoRenew = async (
    subscription: SubscriptionDetails,
    autoRenew: boolean,
    autoRenewalAgreementId?: number,
  ) => {
    const updated = await updateSubscription({
      accountId: account!.id,
      subscriptionId: subscription.id,
      autoRenew,
      autoRenewalAgreementId,
    });
    updateFetchAccountBffCache(updated);
  };

  const openConsentModal = async (subscription: SubscriptionDetails) => {
    const agreements = await fetchAgreements(
      "AUTO_RENEWAL",
      subscription.planPackage.id,
      subscription.billingInterval,
    );
    if (agreements.length === 0) return false;
    setPendingSubscription(subscription);
    setPendingAgreement(agreements[0]);
    return true;
  };

  const handleAutoRenewChange = async (
    subscription: SubscriptionDetails,
    checked: boolean,
  ) => {
    if (!account) return;
    if (!checked) {
      await patchAutoRenew(subscription, false);
      return;
    }
    // Consent-required plans return an agreement here; the modal collects
    // consent before enabling. An empty result means no consent is needed.
    if (await openConsentModal(subscription)) return;
    try {
      await patchAutoRenew(subscription, true);
    } catch (error: any) {
      if (error?.data?.code === "AUTO_RENEWAL_CONSENT_REQUIRED") {
        await openConsentModal(subscription);
      }
    }
  };

  const closeConsentModal = () => {
    setPendingSubscription(null);
    setPendingAgreement(null);
  };

  const handleConsentAccepted = async () => {
    if (!pendingSubscription || !pendingAgreement) return;
    const subscription = pendingSubscription;
    const agreement = pendingAgreement;
    closeConsentModal();
    await patchAutoRenew(subscription, true, agreement.id);
  };

  if (!account || autoRenewSubscriptions.length === 0) return null;

  return (
    <BaseCard title="Auto-Renewal">
      <Text
        appearance="hint"
        category="c1"
        style={{ marginTop: 4, marginBottom: 8 }}
      >
        Auto-renewal is managed per plan. Turn it on for each plan you want to
        renew automatically on the date specified in its auto-renewal terms.
      </Text>
      {autoRenewSubscriptions.map((subscription) => (
        <BaseCardListItem
          key={subscription.id}
          title={
            subscription.planPackage.displayName ||
            subscription.planPackage.name
          }
          description={
            subscription.member
              ? `${subscription.member.user.firstName} ${subscription.member.user.lastName}`
              : undefined
          }
          accessoryRight={
            <Switch
              testID={`auto-renew-switch-${subscription.id}`}
              checked={subscription.autoRenew}
              onChange={(checked) =>
                handleAutoRenewChange(subscription, checked)
              }
            />
          }
          onPress={() =>
            handleAutoRenewChange(subscription, !subscription.autoRenew)
          }
        />
      ))}

      <AutoRenewalDisclosureModal
        visible={!!pendingSubscription && !!pendingAgreement}
        onClose={closeConsentModal}
        onAccept={handleConsentAccepted}
        agreementBody={pendingAgreement?.body ?? ""}
      />
    </BaseCard>
  );
};

export default AutoRenewalCard;
